import { Component, Input, Output, EventEmitter } from '@angular/core';
import { Event } from './events.service';
import { format, isSameDay } from 'date-fns';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';

import { MatCardModule } from '@angular/material/card';
import { EventShareComponent } from './event-share.component';
import {
  formatUTCToLocalWithTimezone,
  formatDateShort,
  getTimezoneAbbreviation,
  getUserTimezone
} from '../shared/timezone.utils';
import { getOptimizedImageUrl } from '../shared/image.utils';
@Component({
  selector: 'app-event-card',
   imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    EventShareComponent
  ],
  template: `
    <mat-card class="event-card" (click)="onViewDetails()">
      <div class="event-image" *ngIf="event.image">
          <img [src]="getCardImageUrl()" [alt]="event.title">

      </div>
      <div class="event-image-placeholder" *ngIf="!event.image">
        <mat-icon>event</mat-icon>
      </div>

      <mat-card-content>
        <h3 class="event-title">{{ event.title }}</h3>

        <div class="event-info">
          <div class="info-item">
            <mat-icon>calendar_today</mat-icon>
            <span>{{ formatDate(event.start) }}</span>
          </div>

          <div class="info-item">
            <mat-icon>schedule</mat-icon>
            <span>{{ formatTime(event.start) }}</span>
          </div>

          <div class="info-item">
            <mat-icon>category</mat-icon>
            <span class="capitalize">{{ event.type }}</span>
          </div>

          <div class="info-item">
            <mat-icon>videocam</mat-icon>
            <span>{{ event.platform }}</span>
          </div>
        </div>

        <p class="event-description" *ngIf="event.description">
          {{ event.description | slice:0:100 }}{{ event.description.length > 100 ? '...' : '' }}
        </p>
      </mat-card-content>

      <mat-card-actions>
        <button mat-raised-button color="primary" (click)="onViewDetails(); $event.stopPropagation()">
          View Details
        </button>
          <app-event-share [event]="event" (click)="$event.stopPropagation()"></app-event-share>

      </mat-card-actions>
    </mat-card>
  `,
  styles: [`
    .event-card {
      cursor: pointer;
      transition: transform 0.2s, box-shadow 0.2s;
      height: 100%;
      display: flex;
      flex-direction: column;

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
      }
    }

    .event-image {
  width: 100%;
  height: 200px;           
  overflow: hidden;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

    .event-image-placeholder {
      width: 100%;
      height: 200px;
      background: linear-gradient(135deg, #007664 0%, #005a4d 100%);
      display: flex;
      align-items: center;
      justify-content: center;

      mat-icon {
        font-size: 4rem;
        width: 4rem;
        height: 4rem;
        color: white;
      }
    }

    mat-card-content {
      flex: 1;
      padding: 1rem;
    }

    .event-title {
      font-size: 1.25rem;
      font-weight: 600;
      margin: 0 0 1rem 0;
      color: #333;
      text-transform: capitalize;
    }

    .event-info {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      margin-bottom: 1rem;
    }

    .info-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #666;
      font-size: 0.875rem;

      mat-icon {
        font-size: 1rem;
        width: 1rem;
        height: 1rem;
        color: #007664;
      }

      .capitalize {
        text-transform: capitalize;
      }
    }

    .event-description {
      color: #666;
      font-size: 0.875rem;
      line-height: 1.5;
      margin: 0;
    }

    mat-card-actions {
      padding: 1rem;
      border-top: 1px solid #e0e0e0;

      button {
        width: 100%;
        background-color: #007664 !important;
        color: white !important;

        &:hover {
          background-color: #005a4d !important;
        }
      }
    }
  `]
})
export class EventCardComponent {
  @Input() event!: Event;
  @Output() viewDetails = new EventEmitter<void>();
getCardImageUrl(): string {
  return getOptimizedImageUrl(this.event.image, 400, 200);
}
  formatDate(dateString: string): string {
    try {
      return formatDateShort(dateString);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  }

  formatTime(dateString: string): string {
    try {
      const date = new Date(dateString + 'Z');

      const timeFormat = new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: getUserTimezone()
      });

      const time = timeFormat.format(date);
      const timezone = getTimezoneAbbreviation();

      return `${time} ${timezone}`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return 'Invalid time';
    }
  }

  onViewDetails(): void {
    this.viewDetails.emit();
  }
}
